import { useEffect, useState } from "react";
import { X, Server, Plus } from "lucide-react";
import { useCreateDockerHost } from "@/hooks/useDocker";
import { useActiveTenant } from "@/components/tenant/TenantSelector";
import { estimatePricing } from "@/api/endpoints/billing";

interface Props {
  onClose: () => void;
}

const TIERS: { id: "A" | "B" | "C"; label: string }[] = [
  { id: "A", label: "Solo" },
  { id: "B", label: "Actif/Passif" },
  { id: "C", label: "Actif/Actif" },
];

/** Provisioning wizard for a docker-host VM (mirrors the VM provisioning UX). */
export function DockerHostWizard({ onClose }: Props) {
  const [tenantId] = useActiveTenant();
  const createHost = useCreateDockerHost();
  const [form, setForm] = useState({
    name: "",
    service_tier: "A" as "A" | "B" | "C",
    cores: 4,
    memory_mb: 8192,
    disk_gb: 40,
  });
  const [cost, setCost] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    estimatePricing({
      cores: form.cores,
      memory_mb: form.memory_mb,
      disk_gb: form.disk_gb,
      service_tier: form.service_tier,
      network_speed: "100m",
      backup_plan: "none",
    })
      .then((e) => alive && setCost(e.total_monthly))
      .catch(() => alive && setCost(null));
    return () => {
      alive = false;
    };
  }, [form.cores, form.memory_mb, form.disk_gb, form.service_tier]);

  function submit() {
    if (!form.name || !tenantId) return;
    createHost.mutate(
      { ...form, tenant_id: Number(tenantId) },
      { onSuccess: onClose },
    );
  }

  const num = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((s) => ({ ...s, [k]: Number(e.target.value) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
      <div className="w-full max-w-lg rounded-lg border border-gray-700 bg-gray-950">
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
          <div className="flex items-center gap-2 text-gray-100">
            <Server className="h-4 w-4" /> Nouveau docker-host
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div>
            <label className="text-xs text-gray-400">Nom</label>
            <input
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="docker-1"
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 placeholder-gray-500"
            />
          </div>

          <div>
            <label className="text-xs text-gray-400">Niveau de service</label>
            <div className="mt-1 flex gap-2">
              {TIERS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setForm((s) => ({ ...s, service_tier: t.id }))}
                  className={`flex-1 rounded border px-3 py-2 text-sm ${
                    form.service_tier === t.id
                      ? "border-blue-500 bg-blue-900/30 text-blue-200"
                      : "border-gray-700 text-gray-300 hover:bg-gray-800"
                  }`}
                >
                  {t.id} · {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-400">vCPU</label>
              <input
                type="number"
                min={1}
                max={64}
                value={form.cores}
                onChange={num("cores")}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-2 py-2 text-sm text-gray-100"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">RAM (Mo)</label>
              <input
                type="number"
                min={1024}
                step={1024}
                value={form.memory_mb}
                onChange={num("memory_mb")}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-2 py-2 text-sm text-gray-100"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400">Disque (Go)</label>
              <input
                type="number"
                min={10}
                step={10}
                value={form.disk_gb}
                onChange={num("disk_gb")}
                className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-2 py-2 text-sm text-gray-100"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded border border-gray-800 px-3 py-2">
            <span className="text-sm text-gray-400">Coût estimé</span>
            <span className="text-sm font-semibold text-green-300">
              {cost !== null ? `${cost.toFixed(2)} €/mois` : "…"}
            </span>
          </div>

          <button
            onClick={submit}
            disabled={!form.name || !tenantId || createHost.isPending}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            {createHost.isPending ? "Provisionnement…" : "Provisionner"}
          </button>
          <p className="text-xs text-gray-500">
            Provisionne une VM Ubuntu, installe Docker + l'agent et l'enregistre.
            Statut <code>ready</code> une fois l'agent joignable.
          </p>
        </div>
      </div>
    </div>
  );
}
